import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import FileUpload from '@/components/FileUpload';
import Button from '@/design_system/Button';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { getRefashionStatus } from '@/models/actionType';
import { getArticlePhoto, getProductPictureFromArticleColor, hasDefects } from '@/models/article';
import { Medium } from '@/models/medium';
import { hasColorOptions } from '@/models/product';
import { ProductAttribute, ProductAttributeValueWithBrand } from '@/models/productAttribute';
import { ArticleWithRelations } from '@/models/request';
import { hasSizeOptions } from '@/models/sizeProduct';
import { ProgressBar } from '@/routes/Brand/Requests/New/components/ProgressBar/ProgressBar';
import { ProductPhoto } from '@/routes/Requests/Request/Article/ProductPhoto';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { AdditionalInfo } from './components/AdditionalInfo';
import ProductInfo from './components/ProductInfo';

import './ArticleForm.css';

const { block } = createBEMClasses('client-article-form');

const ArticleForm = ({
  onContinue,
  onBack,
  article,
  proofOfPurchaseMedia,
}: {
  onContinue: () => void;
  onBack: () => void;
  article: ArticleWithRelations;
  proofOfPurchaseMedia: Medium[];
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { view, productAttributesData } = useBrandRequestContext();

  const productImage = getProductPictureFromArticleColor({ article });

  const hasRefashionAction =
    article?.snapshot?.articleActions?.some(
      (action) => getRefashionStatus(action.organizationPrice) === 'applied'
    ) ?? false;

  const showArticlePhotoOptional =
    !view.articles.details.articlePhoto.required && !hasRefashionAction;

  const isColorInvalid =
    view.articles.details.color.shown &&
    view.articles.details.color.required &&
    hasColorOptions({ article }) &&
    !article.color;

  const isSizeInvalid =
    view.articles.details.size.shown &&
    view.articles.details.size.required &&
    hasSizeOptions({ article }) &&
    !article.size;

  const shouldCheckWarrantyErrors = !!article.activeWarrantyType;

  const isPurchaseDateInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.purchaseDate.shown &&
    ((view.articles.warranty.fields.purchaseDate.required && !article.purchaseDate) ||
      (!!article.purchaseDate && parseISO(article.purchaseDate) > new Date()));

  const isPurchaseLocationInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.purchaseLocation.shown &&
    view.articles.warranty.fields.purchaseLocation.required &&
    !article.purchaseLocation;

  const isPurchaseCountryInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.purchaseCountry.shown &&
    view.articles.warranty.fields.purchaseCountry.required &&
    !article.purchaseCountry;

  const isPurchaseChannelInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.purchaseChannel.shown &&
    view.articles.warranty.fields.purchaseChannel.required &&
    !article.purchaseChannel;

  const isOrderNumberInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.orderNumber.shown &&
    view.articles.warranty.fields.orderNumber.required &&
    !article.orderNumber;

  const isProofOfPurchaseInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.proofOfPurchase.shown &&
    view.articles.warranty.fields.proofOfPurchase.required &&
    !proofOfPurchaseMedia.length;

  const isProofOfPurchaseOrOrderNumberInvalid =
    shouldCheckWarrantyErrors &&
    view.articles.warranty.fields.proofOfPurchase.shown &&
    view.articles.warranty.fields.orderNumber.shown &&
    view.articles.warranty.fields.proofOfPurchaseOrOrderNumber.required &&
    !proofOfPurchaseMedia.length &&
    !article.orderNumber;

  const selectedProductAttributeValueIds = article.productAttributeValues.map((value) => value.id);
  const productAttributes =
    productAttributesData.find(({ brandId }) => brandId === article?.brandId)?.productAttributes ??
    [];
  const hasRequiredProductAttributeValues = productAttributes
    .filter(
      (productAttribute: ProductAttribute) => productAttribute.enabled && productAttribute.required
    )
    .filter(
      (productAttribute: ProductAttribute) =>
        !productAttribute.parentProductAttributeValueId ||
        selectedProductAttributeValueIds.includes(productAttribute.parentProductAttributeValueId)
    )
    .every((productAttribute: ProductAttribute) =>
      productAttribute.productAttributeValues.some((value: ProductAttributeValueWithBrand) =>
        selectedProductAttributeValueIds.includes(value.id)
      )
    );

  const articlePhotosInvalid =
    view.articles.details.articlePhoto.shown &&
    view.articles.details.articlePhoto.required &&
    !getArticlePhoto(article);

  const disableContinue =
    (!article.product && !hasRequiredProductAttributeValues) ||
    articlePhotosInvalid ||
    isColorInvalid ||
    isSizeInvalid ||
    isPurchaseDateInvalid ||
    isPurchaseLocationInvalid ||
    isPurchaseCountryInvalid ||
    isPurchaseChannelInvalid ||
    isOrderNumberInvalid ||
    isProofOfPurchaseInvalid ||
    isProofOfPurchaseOrOrderNumberInvalid;

  const progress =
    article.product || hasRequiredProductAttributeValues
      ? getArticlePhoto(article)
        ? 45
        : 35
      : 10;

  return (
    <div className={block()}>
      <main>
        <Stack gap="1.5rem">
          <Title size="300" as="h3">
            <Trans id="client.new.article.form.title">Tell us which item needs attention</Trans>
          </Title>
          <Stack gap="3rem">
            <ProductInfo article={article} disabled={hasDefects(article)} />

            {(!!article.product || hasRequiredProductAttributeValues) &&
              view.articles.details.articlePhoto.shown && (
                <FileUpload
                  uploadData={{
                    type: 'article-photo',
                    articleId: article.id,
                  }}
                  type="photo"
                  label={
                    <>
                      {t({
                        id: 'client.new.article.form.photo.label',
                        message: 'Please upload a full-view photo of your item',
                      })}
                      {showArticlePhotoOptional && <OptionalLabel />}
                    </>
                  }
                  ariaLabel={t({
                    id: 'client.new.article.form.photo.label',
                    message: 'Please upload a full-view photo of your item',
                  })}
                  maxNumberOfMedia={1}
                  media={getArticlePhoto(article) ? [getArticlePhoto(article)!] : []}
                  size="xxlarge"
                  theme="brand"
                  onMount={() => {
                    window.scroll({
                      top: document.body.scrollHeight,
                      behavior: 'smooth',
                    });
                  }}
                  deleteWithApi
                />
              )}

            {!!article.product && !!productImage && (
              <ProductPhoto productName={article.product.name} productImage={productImage} />
            )}

            {(!!article.product || hasRequiredProductAttributeValues) && !articlePhotosInvalid && (
              <AdditionalInfo article={article} proofOfPurchaseMedia={proofOfPurchaseMedia} />
            )}
          </Stack>
        </Stack>
      </main>
      <footer>
        <ProgressBar progress={progress} />
        <Stack row gap={isMobile ? '1rem' : '2rem'}>
          <Button size="large" variant="secondary-brand" style={{ flex: 1 }} onPress={onBack}>
            <IconArrow left />
            <Trans id="client.new.article.form.action.back">Back</Trans>
          </Button>
          <Button
            size="large"
            variant="brand"
            style={{ flex: 1 }}
            disabled={disableContinue}
            onPress={onContinue}
          >
            <Trans id="client.new.article.form.action.continue">Continue</Trans>
            <IconArrow right />
          </Button>
        </Stack>
      </footer>
    </div>
  );
};

export default ArticleForm;
